import { useQuery, useQueryClient } from '@tanstack/react-query'

import { consulta, pedir } from './nucleo'

/**
 * «Publicar mi web» y las acciones en lote (CLAUDE.md §13, §20). Qué hay pendiente y qué
 * se puede hacer con el diseño lo dice el núcleo (`/panel/web/pendiente`), para quien
 * pregunta; aquí solo se pinta y se manda **lo que se ha enseñado**, por id.
 */

/** Lo que se publica desde «Publicar mi web»: el contenido de siempre, los contenidos de los tipos y la estructura (tipos, taxonomías y grupos de campos). */
export type ColeccionPublicable = 'contenidos' | 'entradas' | 'grupos-de-campos' | 'paginas' | 'productos' | 'servicios' | 'taxonomias' | 'tipos-de-contenido'
/** Las de la estructura de la web: van juntas, arriba, y se publican antes que el contenido. */
export type ColeccionDeEstructura = 'grupos-de-campos' | 'taxonomias' | 'tipos-de-contenido'
export type AccionDeLote = 'borrar' | 'despublicar' | 'publicar'

type Comun = { id: number; cambios: boolean }
export type PaginaPendiente = Comun & { titulo: string; slug: string; portada: boolean; porRellenar: boolean; seccionesVacias: string[] }
export type ProductoPendiente = Comun & { nombre: string; sinPrecio: boolean; sinImagen: boolean }
export type EntradaPendiente = Comun & { titulo: string }
/** `sinPrecio`: dice «Precio fijo» o «Desde…» y no tiene número (así no se publica). Con «A consultar», nunca. */
export type ServicioPendiente = Comun & { nombre: string; sinPrecio: boolean; sinImagen: boolean }
/** Un contenido de un tipo a medida: `tipo` es el nombre del tipo en singular («Propiedad»). */
export type ContenidoPendiente = Comun & { titulo: string; tipo: string; tipoId?: null | number }
/** Una definición de la estructura: un tipo de contenido, una taxonomía o un grupo de campos. */
export type DefinicionPendiente = Comun & { nombre: string }

export type Grupo<T> = {
  publicados: number
  /** Lo que nunca se publicó, o se despublicó. */
  enBorrador: number
  /** Lo publicado con cambios guardados encima: en internet sigue lo de antes. */
  conCambios: number
  borradores: T[]
  /** Lo pendiente que no entra en la lista (el núcleo enseña 200 de cada tipo). */
  fuera: number
}

export type SituacionDelDiseno = 'al-dia' | 'construyendo' | 'fallida' | 'lista' | 'retirada' | 'sin-construir' | 'sin-version' | 'solicitada'

export type DisenoPendiente = {
  publicada: number
  flujo: 'aprobacion' | 'directa'
  puede: 'nada' | 'publicar' | 'solicitar'
  motivo: null | string
  version: null | { numero: number; comentario: string; estado: string }
  situacion: SituacionDelDiseno
  texto: string
  pendiente: boolean
  solicitud: null | { id: number; version: number }
}

export type Pendiente = {
  sitio: number
  cliente: number
  nombre: string
  dominio: string
  enInternet: boolean
  paginas: null | (Grupo<PaginaPendiente> & { inicioPublicado: boolean; publicadasPorRellenar: Array<{ id: number; titulo: string }> })
  productos: null | Grupo<ProductoPendiente>
  /** `null` si la web no tiene el catálogo de servicios. */
  servicios?: null | Grupo<ServicioPendiente>
  entradas: null | Grupo<EntradaPendiente>
  /** Los contenidos de los tipos; `null` (o ausente, en un núcleo de antes) sin el módulo `estructura`. */
  contenidos?: null | Grupo<ContenidoPendiente>
  /** Lo pendiente de la estructura; `null` sin el módulo `estructura`. */
  estructura?: null | { tiposDeContenido: Grupo<DefinicionPendiente>; taxonomias: Grupo<DefinicionPendiente>; gruposDeCampos: Grupo<DefinicionPendiente> }
  diseno: DisenoPendiente
  total: number
}

export type ElementoHecho = { id: number; titulo: string }
export type ElementoFallido = ElementoHecho & { motivo: string; campos?: string[] }
export type ResultadoDeLote = { hechos: ElementoHecho[]; yaEstaban: ElementoHecho[]; fallidos: ElementoFallido[] }
export type ResultadoDelDiseno = { accion: 'ninguna' | 'publicado' | 'solicitado'; version: null | number; motivo: null | string }
export type PublicacionDeLaWeb = Record<ColeccionPublicable, ResultadoDeLote> & { diseno: null | ResultadoDelDiseno }

export type PedidoDePublicacion = {
  paginas: number[]
  productos: number[]
  servicios: number[]
  entradas: number[]
  contenidos: number[]
  tiposDeContenido: number[]
  taxonomias: number[]
  gruposDeCampos: number[]
  diseno: boolean
  /** La versión que se enseñó y la que se cree publicada (§18): lo que se publica es lo que se ha visto. */
  version?: number
  esperada?: number
}

/** Lo que queda por publicar. Con `cuentas`, sin las listas: es lo que pide el número del botón en cada pantalla. */
export const usePendiente = (sitio: null | number, o: { cuentas?: boolean; activa?: boolean } = {}) =>
  useQuery({
    queryKey: ['web-pendiente', sitio, o.cuentas ? 'cuentas' : 'entero'],
    queryFn: () => pedir<Pendiente>(`/panel/web/pendiente${consulta({ sitio: sitio ?? undefined, cuentas: o.cuentas ? '1' : undefined })}`),
    enabled: sitio !== null && o.activa !== false,
    staleTime: 20_000,
  })

export const publicarLaWeb = (sitio: number, pedido: PedidoDePublicacion) => pedir<PublicacionDeLaWeb>('/panel/web/publicar', { metodo: 'POST', cuerpo: { sitio, ...pedido } })

export const aplicarEnLote = (coleccion: string, sitio: number, ids: number[], accion: AccionDeLote) =>
  pedir<ResultadoDeLote & { coleccion: string; accion: AccionDeLote }>('/panel/lote', { metodo: 'POST', cuerpo: { coleccion, sitio, ids, accion } })

/** La dirección de previa de una versión (enlace firmado de corta duración). */
export const previaDe = (sitio: number, version: number) => pedir<{ url: string; caduca: string }>('/versiones-de-tema/previa', { metodo: 'POST', cuerpo: { sitio, version } })

/**
 * Lo que hay que olvidar después de publicar o de tocar varios a la vez: lo pendiente,
 * los listados y el estado del diseño de esa web.
 */
export const useRefrescarPublicacion = () => {
  const clienteDeConsultas = useQueryClient()
  return (sitio: number) => {
    for (const clave of ['web-pendiente', 'diseno-estado', 'versiones']) void clienteDeConsultas.invalidateQueries({ queryKey: [clave, sitio] })
    for (const clave of ['lista', 'paginas', 'doc']) void clienteDeConsultas.invalidateQueries({ queryKey: [clave] })
  }
}
